import React, { useEffect, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import { useStoreContext } from 'hooks/StoreContext'
import useSiteMessages from 'hooks/useSiteMessages'
import useAutoFocus from 'hooks/useAutoFocus'

import Tabs from 'components/Tabs'
import UpdateDetail from 'pages/Updates/UpdateDetail'

import Api from 'services/api'
import GA4 from 'helpers/ga4'

import { LoadingSinglePanePage, PageHeader, PageTitle, SinglePanePage, screenSize } from 'styles/shared'

import { useConfirmDialog } from 'hooks/useDialog'

import { CONFIRMATION_DIALOG_MESSAGES } from 'helpers/confirmationDialogMessages'

const TABS = ['current', 'archived']

const Updates = () => {
  useSiteMessages({ getPublishedOnly: true })

  const title = useRef(null)
  const navigate = useNavigate()
  const { tab } = useParams()

  const { siteMessages, areSiteMessagesLoaded } = useStoreContext()
  const [unreadSiteMessageIds, setUnreadSiteMessageIds] = useState(null)
  const currentTab = tab && TABS.includes(tab) ? tab : TABS[0]
  const activeTabIndex = TABS.indexOf(currentTab)
  const [areUnreadSiteMessageIdsLoaded, setAreUnreadSiteMessageIdsLoaded] = useState(false)
  const [areArchivedSiteMessageIdsLoaded, setAreArchivedSiteMessageIdsLoaded] = useState(false)
  const [areRemovedSiteMessageIdsLoaded, setAreRemovedSiteMessageIdsLoaded] = useState(false)
  const [filteredSiteMessages, setFilteredSiteMessages] = useState([])
  const [archivedSiteMessages, setArchivedSiteMessages] = useState([])
  const [refreshTimestamp, setRefreshTimestamp] = useState(Date.now())
  const { confirm } = useConfirmDialog()
  const componentType = 'Message'

  const isLoaded = areUnreadSiteMessageIdsLoaded && areArchivedSiteMessageIdsLoaded && areRemovedSiteMessageIdsLoaded

  useAutoFocus({ isLoaded, element: title })

  useEffect(() => {
    let cancelled = false

    if (areSiteMessagesLoaded) {
      Api.SiteMessagesUserRead.getUnread().then((res) => {
        if (cancelled) return

        setUnreadSiteMessageIds(res.siteMessageIds)
        setAreUnreadSiteMessageIdsLoaded(true)

        Api.SiteMessagesUserRead.create(res).then(() => {
          window.dispatchEvent(new Event('site-messages-read'))
        })
      })

      Api.SiteMessagesUserRead.getArchived().then((res) => {
        if (cancelled) return

        setArchivedSiteMessages(siteMessages.filter((siteMessage) => res.siteMessageIds?.includes(siteMessage.id)))
        setAreArchivedSiteMessageIdsLoaded(true)
      })

      Api.SiteMessagesUserRead.getRemoved().then((res) => {
        if (cancelled) return

        setFilteredSiteMessages(siteMessages.filter((siteMessage) => !res.siteMessageIds?.includes(siteMessage.id)))
        setAreRemovedSiteMessageIdsLoaded(true)
      })
    }

    return () => { cancelled = true }
  }, [areSiteMessagesLoaded, refreshTimestamp, siteMessages])

  const onClickCallback = (action) => {
    const eventData = { category: 'Updates', action }

    GA4.sendEvent(eventData)
  }

  const onClickAction = async (siteMessageId, action) => {
    if (action === 'archive') {
      await Api.SiteMessagesUserRead.archive(siteMessageId)
      setRefreshTimestamp(Date.now())
    } else if (action === 'restore') {
      await Api.SiteMessagesUserRead.restore(siteMessageId)
      setRefreshTimestamp(Date.now())
    } else {
      const isConfirmed = await confirm({
        title: CONFIRMATION_DIALOG_MESSAGES.DELETE.TITLE(componentType),
        description: CONFIRMATION_DIALOG_MESSAGES.DELETE.DESCRIPTION(componentType),
        confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.DELETE
      })

      if (isConfirmed) {
        await Api.SiteMessagesUserRead.remove(siteMessageId)
        setRefreshTimestamp(Date.now())
      }
    }
  }

  const handleTabChange = (index) => {
    const tabName = TABS[index] || TABS[0]
    navigate(`/updates/${tabName}`)
  }

  if (!isLoaded) {
    return <LoadingSinglePanePage />
  }

  return (
    <>
      <S.PageHeader>
        <PageTitle ref={title}>Updates</PageTitle>
      </S.PageHeader>
      <SinglePanePage>
        <Tabs
          activeTabIndex={activeTabIndex}
          onSelectTabIndexCallback={handleTabChange}
          tabItems={[
            {
              key: 0,
              header: 'Current',
              details:
  <UpdateDetail
                  filteredSiteMessages={filteredSiteMessages}
                  unreadSiteMessageIds={unreadSiteMessageIds}
                  onClickCallback={onClickCallback}
                  onClickAction={onClickAction}
                  tabSelection={'Current'}
  />
            },
            {
              key: 1,
              header: 'Archived',
              details:
  <UpdateDetail
                  filteredSiteMessages={archivedSiteMessages}
                  unreadSiteMessageIds={[]}
                  onClickCallback={onClickCallback}
                  onClickAction={onClickAction}
                  tabSelection={'Archive'}
  />
            }
          ]}
        />
      </SinglePanePage>
    </>

  )
}

export default Updates

const S = {
  PageHeader: styled(PageHeader)`

    @media ${screenSize.small} {
      display: none;
    }
  `
}
